import { useState } from 'react';
import { Spinner, Text, Wizard, Toast, Image } from '@belong/ui';
import { RescheduleInspectionAppointmentOptionsForm } from 'accounts/components/reschedule-inspection-appointment-options-form/reschedule-inspection-appointment-options-form';
import { STRINGS } from 'accounts/constants/strings/reschedule-appointment';
import {
  useRescheduleAppointment,
  useRescheduleAppointmentContext,
} from 'accounts/hooks/reschedule-appointment';
import type { RescheduleAppointmentOptionsFormSchema } from 'accounts/types';
import { format } from 'date-fns-tz';

const { useWizard } = Wizard;

export function RescheduleAppointmentOptionsStep() {
  const [isErrorToastVisible, setIsErrorToastVisible] = useState(false);
  const { nextStep } = useWizard();
  const {
    setFormattedAppointmentDate,
    timeZone,
    unitId,
    isHandledByAnExternalProvider = false,
  } = useRescheduleAppointmentContext();
  const { isLoading: isReschedulingAppointment, rescheduleAppointment } =
    useRescheduleAppointment();

  function handleToastClose() {
    setIsErrorToastVisible(false);
  }

  async function handleSubmit(values: RescheduleAppointmentOptionsFormSchema) {
    try {
      setIsErrorToastVisible(false);

      const { startTimeOptions } = values;

      if (startTimeOptions && startTimeOptions.length > 0) {
        const payload: {
          unitId: string;
          startTimeOptions?: string[];
          start?: string;
        } = {
          unitId,
        };

        // If handled by external provider, send the first available time as start
        if (isHandledByAnExternalProvider) {
          const [firstTimeOption] = startTimeOptions;
          payload.start = firstTimeOption;
        } else {
          payload.startTimeOptions = startTimeOptions;
        }

        await rescheduleAppointment(payload);

        // Format the first time option for display
        // Inside the `startTimeOptions.length > 0` guard above.
        const firstTimeOption = new Date(startTimeOptions[0]!);
        const formattedAppointmentDate = format(firstTimeOption, 'PPP h:mm a', {
          timeZone,
        });

        setFormattedAppointmentDate(formattedAppointmentDate);

        nextStep();
      }
    } catch {
      setIsErrorToastVisible(true);
    }
  }

  return (
    <>
      {isReschedulingAppointment && <Spinner />}
      <div className="w-full">
        <div className="m-auto mb-2xl max-w-[300px] text-center sm:max-w-[400px]">
          <Text variant="h2" fontWeight="semibold">
            {STRINGS['step.options.title']}
          </Text>
        </div>
        <RescheduleInspectionAppointmentOptionsForm onSubmit={handleSubmit} />
      </div>
      <Toast
        icon={
          <Image
            alt=""
            className="object-scale-down"
            src="icons/alert-white.svg"
          />
        }
        isVisible={isErrorToastVisible}
        onClose={handleToastClose}
        variant="danger"
      >
        {STRINGS['step.options.reschedule.error']}
      </Toast>
    </>
  );
}
